import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Editor } from 'primereact/editor';
import { useDebounce } from 'primereact/hooks';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Password } from 'primereact/password';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabMenu } from 'primereact/tabmenu';
import { useEffect, useMemo, useState } from 'react';

import { useEmployee } from '../../context/employee';
import RelationshipDropdown from '../relationship-dropdown';
import StateDropdown from '../state-dropdown';
import { GetEditorFormats } from '../../shared';

function EmployeeCreate({ open, onClose }) {
    const { CreateEmployee, ValidateUsername } = useEmployee();

    const [activeIndex, setActiveIndex] = useState(0);

    const [active, setActive] = useState(true);
    const [admin, setAdmin] = useState(false);
    const [name, setName] = useState('');
    const [username, dbUsername, setUsername] = useDebounce('', 420);
    const [usernameValid, setUsernameValid] = useState(true);
    const [password, setPassword] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [workExt, setWorkExt] = useState('');
    const [notes, setNotes] = useState('');

    const [homePhone, setHomePhone] = useState('');
    const [workEmail, setWorkEmail] = useState('');
    const [homeEmail, setHomeEmail] = useState('');
    const [streetAddress1, setStreetAddress1] = useState('');
    const [streetAddress2, setStreetAddress2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');

    const [ecName, setEcName] = useState('');
    const [ecPhone, setEcPhone] = useState('');
    const [ecRelationship, setEcRelationship] = useState('');

    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');

    const tabItems = [{ label: 'Basic' }, { label: 'Other' }, { label: 'Emergency Contact' }];

    const buttonDisabled = () => {
        if (!name || !username || !password || !usernameValid || password.length < 8) {
            return true;
        }

        return false;
    };

    const onTabChange = ({ index }) => {
        // TODO - override duplicate name
        // not allow move if name is dup or invalid person data
        if (buttonDisabled()) {
            return alert('Please fill in the required fields before moving on');
        }

        setActiveIndex(index);
    };

    const reset = () => {
        setActiveIndex(0);

        setActive(true);
        setAdmin(false);
        setName('');
        setUsername('');
        setUsernameValid(true);
        setPassword('');
        setJobTitle('');
        setWorkExt('');
        setNotes('');

        setHomePhone('');
        setWorkEmail('');
        setHomeEmail('');
        setStreetAddress1('');
        setStreetAddress2('');
        setCity('');
        setState('');
        setZip('');

        setEcName('');
        setEcPhone('');
        setEcRelationship('');

        setProcessing(false);
        setProcessingError('');
    };

    const onCancel = () => {
        reset();
        onClose({ status: 499, message: 'Canceled' });
    };

    const onCreate = () => {
        const data = {
            active,
            username,
            password,
            tokens: [],
            admin,
            name,
            oldId: '',
            jobTitle,
            workExt,
            workEmail,
            homeEmail,
            homePhone,
            streetAddress1,
            streetAddress2,
            city,
            state,
            zipCode: zip,
            emergencyContactName: ecName,
            emergencyContactPhone: ecPhone,
            notes
        };

        if (ecRelationship) {
            data.emergencyContactRelationship = ecRelationship;
        }

        CreateEmployee(data, (res) => {
            if (res.status !== 200) {
                setProcessingError(res.message);
                setProcessing(false);
            } else {
                reset();
                onClose(res);
            }
        });
    };

    const FooterContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Create"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onCreate}
            />
        </div>
    );

    // Validate Username
    useEffect(() => {
        if (dbUsername) {
            ValidateUsername({ username: dbUsername }, (res) => {
                if (res.status === 200) {
                    setUsernameValid(res.data.isValidUsername);
                }
            });
        }
    }, [dbUsername, ValidateUsername]);

    return (
        <Dialog
            header="Create New Employee"
            visible={open}
            onHide={onCancel}
            footer={FooterContent}
            className="w-9"
        >
            <TabMenu model={tabItems} activeIndex={activeIndex} onTabChange={onTabChange} />

            <ErrorMessage
                error={processingError}
                onClick={() => {
                    setProcessingError('');
                }}
            />

            <ProcessingMessage processing={processing} />

            {activeIndex !== 0 || processing ? null : (
                <TabBasic
                    active={active}
                    setActive={setActive}
                    admin={admin}
                    setAdmin={setAdmin}
                    name={name}
                    setName={setName}
                    username={username}
                    dbUsername={dbUsername}
                    setUsername={setUsername}
                    usernameValid={usernameValid}
                    password={password}
                    setPassword={setPassword}
                    jobTitle={jobTitle}
                    setJobTitle={setJobTitle}
                    workExt={workExt}
                    setWorkExt={setWorkExt}
                    notes={notes}
                    setNotes={setNotes}
                />
            )}

            {activeIndex !== 1 || processing ? null : (
                <TabOther
                    homePhone={homePhone}
                    setHomePhone={setHomePhone}
                    workEmail={workEmail}
                    setWorkEmail={setWorkEmail}
                    homeEmail={homeEmail}
                    setHomeEmail={setHomeEmail}
                    streetAddress1={streetAddress1}
                    setStreetAddress1={setStreetAddress1}
                    streetAddress2={streetAddress2}
                    setStreetAddress2={setStreetAddress2}
                    city={city}
                    setCity={setCity}
                    state={state}
                    setState={setState}
                    zip={zip}
                    setZip={setZip}
                />
            )}

            {activeIndex !== 2 || processing ? null : (
                <TabEmergencyContact
                    ecName={ecName}
                    setEcName={setEcName}
                    ecPhone={ecPhone}
                    setEcPhone={setEcPhone}
                    ecRelationship={ecRelationship}
                    setEcRelationship={setEcRelationship}
                />
            )}
        </Dialog>
    );
}

function ErrorMessage({ error, onClick }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} onClick={onClick} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return (
        <>
            <p>Creating employee...</p>

            <ProgressSpinner />
        </>
    );
}

function TabBasic({
    admin,
    setAdmin,
    active,
    setActive,
    name,
    setName,
    username,
    dbUsername,
    setUsername,
    usernameValid,
    password,
    setPassword,
    jobTitle,
    setJobTitle,
    workExt,
    setWorkExt,
    notes,
    setNotes
}) {
    const getUsernameClassname = useMemo(() => {
        return usernameValid ? '' : 'p-invalid';
    }, [usernameValid]);

    const passwordFooter = (
        <>
            <Divider />

            <p className="text-red-700">Required - 8 Chars</p>

            <p className="mt-2">Suggestions</p>

            <ul className="pl-2 ml-2 mt-0 line-height-3">
                <li>At least one lowercase</li>
                <li>At least one uppercase</li>
                <li>At least one numeric</li>
            </ul>
        </>
    );

    return (
        <div className="mt-2">
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="personActiveId" className="font-bold block mb-2">
                        Active
                    </label>

                    <InputSwitch
                        inputId="personActiveId"
                        checked={active}
                        onChange={(e) => {
                            setActive(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="personAdminId" className="font-bold block mb-2">
                        Admin
                    </label>

                    <InputSwitch
                        inputId="personAdminId"
                        checked={admin}
                        onChange={(e) => {
                            setAdmin(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="nameId" className="font-bold block mb-2">
                        Name
                    </label>

                    <InputText
                        id="nameId"
                        value={name}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setName(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="usernameId" className="font-bold block mb-2">
                        Username
                    </label>

                    <InputText
                        id="usernameId"
                        value={username}
                        className={getUsernameClassname}
                        keyfilter={/^[a-zA-Z0-9]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setUsername(e.target.value);
                        }}
                    />

                    {usernameValid ? null : (
                        <small id="personUsernameId-help">
                            That username ({dbUsername}) is already taken
                        </small>
                    )}
                </div>

                <div className="flex-auto">
                    <label htmlFor="passwordId" className="font-bold block mb-2">
                        Password
                    </label>

                    <Password
                        id="passwordId"
                        minLength={8}
                        toggleMask
                        maxLength={64}
                        footer={passwordFooter}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="jobTitleId" className="font-bold block mb-2">
                        Job Title
                    </label>

                    <InputText
                        id="jobTitleId"
                        value={jobTitle}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setJobTitle(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="workExtId" className="font-bold block mb-2">
                        Work Extension
                    </label>

                    <InputText
                        id="workExtId"
                        value={workExt}
                        keyfilter={/^[0-9]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setWorkExt(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="employeeNotesId" className="font-bold block mb-2">
                        Employee Notes
                    </label>

                    <Editor
                        id="notesId"
                        value={notes}
                        headerTemplate={<GetEditorFormats />}
                        onTextChange={(e) => setNotes(e.htmlValue)}
                        style={{ height: '320px' }}
                    />
                </div>
            </div>
        </div>
    );
}

function TabOther({
    homePhone,
    setHomePhone,
    workEmail,
    setWorkEmail,
    homeEmail,
    setHomeEmail,
    streetAddress1,
    setStreetAddress1,
    streetAddress2,
    setStreetAddress2,
    city,
    setCity,
    state,
    setState,
    zip,
    setZip
}) {
    const getPhoneClassname = useMemo(() => {
        const count = homePhone.length;
        const res = count === 0 || count === 10 ? '' : 'p-invalid';

        return res;
    }, [homePhone]);

    return (
        <div className="mt-2">
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="homePhoneId" className="font-bold block mb-2">
                        Home Phone
                    </label>

                    <InputText
                        id="homePhoneId"
                        value={homePhone}
                        keyfilter={/^[0-9]*$/}
                        minLength={10}
                        className={getPhoneClassname}
                        maxLength={10}
                        onChange={(e) => {
                            setHomePhone(e.target.value);
                        }}
                    />

                    {homePhone.length === 0 || homePhone.length === 10 ? null : (
                        <small id="ecPhoneId-help">
                            The phone number should be exactly 10 digits
                        </small>
                    )}
                </div>

                <div className="flex-auto">
                    <label htmlFor="workEmailId" className="font-bold block mb-2">
                        Work Email
                    </label>

                    <InputText
                        id="workEmailId"
                        value={workEmail}
                        maxLength={42}
                        onChange={(e) => {
                            setWorkEmail(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="homeEmailId" className="font-bold block mb-2">
                        Home Email
                    </label>

                    <InputText
                        id="homeEmailId"
                        value={homeEmail}
                        maxLength={42}
                        onChange={(e) => {
                            setHomeEmail(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="streetAddress1Id" className="font-bold block mb-2">
                        Address Line 1
                    </label>

                    <InputText
                        id="streetAddress1Id"
                        value={streetAddress1}
                        keyfilter={/^[a-zA-Z0-9 ]*$/}
                        maxLength={42}
                        onChange={(e) => {
                            setStreetAddress1(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="streetAddress2Id" className="font-bold block mb-2">
                        Address Line 2
                    </label>

                    <InputText
                        id="streetAddress2Id"
                        value={streetAddress2}
                        keyfilter={/^[a-zA-Z0-9 ]*$/}
                        maxLength={42}
                        onChange={(e) => {
                            setStreetAddress2(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="cityId" className="font-bold block mb-2">
                        City
                    </label>

                    <InputText
                        id="cityId"
                        value={city}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={42}
                        onChange={(e) => {
                            setCity(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="stateId" className="font-bold block mb-2">
                        State
                    </label>

                    <StateDropdown
                        stateId={state}
                        onChange={(stateId) => {
                            setState(stateId);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="zipId" className="font-bold block mb-2">
                        Zip Code
                    </label>

                    <InputText
                        id="zipId"
                        value={zip}
                        keyfilter={/^[0-9]*$/}
                        minLength={5}
                        maxLength={5}
                        onChange={(e) => {
                            setZip(e.target.value);
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

function TabEmergencyContact({
    ecName,
    setEcName,
    ecPhone,
    setEcPhone,
    ecRelationship,
    setEcRelationship
}) {
    const getPhoneClassname = useMemo(() => {
        const count = ecPhone.length;
        const res = count === 0 || count === 10 ? '' : 'p-invalid';

        return res;
    }, [ecPhone]);

    return (
        <div className="mt-2">
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="ecActiveId" className="font-bold block mb-2">
                        Relationship
                    </label>

                    <RelationshipDropdown
                        relationshipId={ecRelationship}
                        onChange={(relationshipId) => {
                            setEcRelationship(relationshipId);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="ecNameId" className="font-bold block mb-2">
                        Name
                    </label>

                    <InputText
                        id="ecNameId"
                        value={ecName}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setEcName(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="ecPhoneId" className="font-bold block mb-2">
                        Phone
                    </label>

                    <InputText
                        id="ecPhoneId"
                        value={ecPhone}
                        className={getPhoneClassname}
                        keyfilter={/^[0-9]*$/}
                        minLength={10}
                        maxLength={10}
                        onChange={(e) => {
                            setEcPhone(e.target.value);
                        }}
                    />

                    {ecPhone.length === 0 || ecPhone.length === 10 ? null : (
                        <small id="ecPhoneId-help">
                            The phone number should be exactly 10 digits
                        </small>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EmployeeCreate;
